"use client";

import { authFetch } from "@/lib/authFetch";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { normalizeAvatarSrc } from "@/utils/avatarUrl";
import {
  formatNotificationAge,
  notificationGlyph,
  showsNotificationTitle,
  type NotificationAgeLabels,
} from "@/lib/notificationPresentation";
import { NotificationGlyphIcon } from "./NotificationGlyphIcon";

const shouldLogDebug = process.env.NODE_ENV !== "production";

interface HubNotification {
  id: string;
  user_id: string;
  category?: string;
  type: string;
  title: string;
  body: string;
  link?: string | null;
  data?: string | Record<string, unknown> | null;
  read: boolean;
  created_at: string;
}

/** Actionable incoming friend request mapped from a hub social notification. */
interface FriendRequest {
  hubNotificationId: string;
  RequestID: string;
  ID: string;
  Username: string;
  DisplayName?: string;
  AvatarKey?: string;
  RequestCreatedAt: string;
  id?: string;
  follower_id?: string;
  follower_username?: string;
  follower_display_name?: string;
  follower_avatar_key?: string;
  created_at?: string;
}

interface NotificationDropdownProps {
  onClose: () => void;
  onRefresh: (count: number) => void;
}

/** Parses notification data JSON into a string map. */
function parseNotificationData(
  data: HubNotification["data"],
): Record<string, string> {
  if (!data) {
    return {};
  }
  try {
    const parsed = typeof data === "string" ? JSON.parse(data) : data;
    if (parsed && typeof parsed === "object") {
      const out: Record<string, string> = {};
      for (const [key, value] of Object.entries(parsed)) {
        if (typeof value === "string") {
          out[key] = value;
        }
      }
      return out;
    }
  } catch {
    // fall through
  }
  return {};
}

/** Resolves a display label from enriched data or legacy body copy. */
function socialDisplayName(notification: HubNotification): string {
  const data = parseNotificationData(notification.data);
  if (data.display_name) {
    return data.display_name;
  }
  if (data.username) {
    return data.username;
  }
  if (notification.type === "friend_request_created") {
    const fromBody = notification.body.replace(" quiere seguirte.", "").trim();
    if (fromBody && fromBody !== "Alguien") {
      return fromBody;
    }
  }
  if (notification.body) {
    return notification.body;
  }
  return notification.title;
}

/** Per-row entrance stagger, mirrors the mobile hub (35ms per row). */
const ROW_STAGGER_MS = 35;

/**
 * NotificationDropdown is the header notifications panel. It mirrors the mobile
 * hub: a "Notificaciones" header with a ghost-danger "Limpiar todo" pill, an
 * unread hint, tone-coloured glyph rows with title/body/age and a lime unread
 * dot, inline Aceptar/Rechazar for friend requests, and an "Estás al día"
 * empty state. Action errors render inline instead of a browser alert.
 */
export function NotificationDropdown({ onRefresh }: NotificationDropdownProps) {
  const t = useTranslations("layout");
  const locale = useLocale();
  const [friendRequests, setFriendRequests] = useState<FriendRequest[]>([]);
  const [socialNotifications, setSocialNotifications] = useState<HubNotification[]>([]);
  const [hubNotifications, setHubNotifications] = useState<HubNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);
  const [dismissing, setDismissing] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const ageLabels = useMemo<NotificationAgeLabels>(
    () => ({
      now: t("notifications.age.now"),
      minutes: (count) => t("notifications.age.minutes", { count }),
      hours: (count) => t("notifications.age.hours", { count }),
      days: (count) => t("notifications.age.days", { count }),
    }),
    [t],
  );

  /** Syncs the bell badge with the hub unread-count endpoint. */
  const syncUnreadCount = useCallback(async () => {
    try {
      const response = await authFetch(
        "/api/notifications/unread-count?category=transaction,auction,social",
        { cache: "no-store" },
      );
      if (response.ok) {
        const data = await response.json();
        onRefresh(data.count ?? 0);
      }
    } catch (error) {
      console.error("[NotificationDropdown] Failed to sync unread count:", error);
    }
  }, [onRefresh]);

  /** Marks a hub notification as read on the server. */
  const markHubNotificationRead = useCallback(async (notificationId: string) => {
    const response = await authFetch(`/api/notifications/${notificationId}/read`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    });
    if (!response.ok) {
      console.error("[NotificationDropdown] Mark read failed:", { notificationId });
      return false;
    }
    return true;
  }, []);

  useEffect(() => {
    void fetchNotifications();
  }, []);

  /** Loads hub notifications and splits them into activity, social info, and friend requests. */
  const fetchNotifications = async () => {
    try {
      if (shouldLogDebug) {
        console.log("[NotificationDropdown] Fetching hub notifications...");
      }
      const response = await authFetch(
        "/api/notifications?limit=20&category=transaction,auction,social",
        { cache: "no-store" },
      );

      if (!response.ok) {
        console.error("[NotificationDropdown] Hub fetch failed", { status: response.status });
        setLoading(false);
        return;
      }

      const data = await response.json();
      const hubItems: HubNotification[] = data.notifications || [];

      setHubNotifications(hubItems.filter((n) => n.category !== "social"));

      const socialPending = hubItems.filter(
        (n) =>
          n.category === "social" &&
          n.type === "friend_request_created" &&
          !n.read,
      );
      const mappedRequests: FriendRequest[] = socialPending.map((n) => {
        const parsed = parseNotificationData(n.data);
        const requestId = parsed.request_id || n.id;
        const followerId = parsed.follower_id || "";
        return {
          hubNotificationId: n.id,
          RequestID: requestId,
          ID: followerId,
          Username: parsed.username || n.title,
          DisplayName: parsed.display_name || socialDisplayName(n),
          AvatarKey: parsed.avatar_key,
          RequestCreatedAt: n.created_at,
          id: requestId,
          follower_id: followerId,
        };
      });
      setFriendRequests(mappedRequests);

      setSocialNotifications(
        hubItems.filter(
          (n) =>
            n.category === "social" &&
            (n.type === "friend_request_accepted" ||
              n.type === "friend_request_rejected") &&
            !n.read,
        ),
      );

      const unreadHub = hubItems.filter((n) => !n.read).length;
      onRefresh(unreadHub);
    } catch (error) {
      console.error("[NotificationDropdown] Failed to fetch:", error);
    } finally {
      setLoading(false);
    }
  };

  const unreadActivity = hubNotifications.filter((n) => !n.read);
  const totalUnread =
    unreadActivity.length + friendRequests.length + socialNotifications.length;

  const handleApprove = async (request: FriendRequest) => {
    const requestId = request.RequestID || request.id || "";
    setActionError(null);
    setProcessing(requestId);
    try {
      const response = await authFetch(`/api/users/follow_requests/${requestId}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      });

      if (response.ok) {
        await markHubNotificationRead(request.hubNotificationId);
        setFriendRequests((prev) =>
          prev.filter((req) => (req.RequestID || req.id) !== requestId),
        );
        await syncUnreadCount();
      } else {
        const errorData = await response
          .json()
          .catch(() => ({ error: t("notifications.errors.approveFailed") }));
        console.error("[NotificationDropdown] Approve failed:", errorData);
        setActionError(errorData.error || t("notifications.errors.approveFailed"));
      }
    } catch (error) {
      console.error("[NotificationDropdown] Approve failed:", error);
      setActionError(t("notifications.errors.approveError"));
    } finally {
      setProcessing(null);
    }
  };

  /** Marks one activity notification as read. */
  const handleDismissActivity = async (notificationId: string) => {
    setDismissing(notificationId);
    try {
      const ok = await markHubNotificationRead(notificationId);
      if (!ok) {
        return;
      }

      setHubNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n)),
      );
      await syncUnreadCount();
    } catch (error) {
      console.error("[NotificationDropdown] Dismiss failed:", error);
    } finally {
      setDismissing(null);
    }
  };

  /** Marks one informational social notification as read. */
  const handleDismissSocial = async (notificationId: string) => {
    setDismissing(notificationId);
    try {
      const ok = await markHubNotificationRead(notificationId);
      if (!ok) {
        return;
      }

      setSocialNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      await syncUnreadCount();
    } catch (error) {
      console.error("[NotificationDropdown] Social dismiss failed:", error);
    } finally {
      setDismissing(null);
    }
  };

  /** Marks all unread notifications as read without changing friendship state. */
  const handleClearAll = async () => {
    setDismissing("all");
    try {
      const hubIds = [
        ...unreadActivity.map((n) => n.id),
        ...socialNotifications.map((n) => n.id),
        ...friendRequests.map((req) => req.hubNotificationId),
      ];

      await Promise.all(
        hubIds.map((id) =>
          authFetch(`/api/notifications/${id}/read`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: "{}",
          }),
        ),
      );

      setHubNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setSocialNotifications([]);
      setFriendRequests([]);
      await syncUnreadCount();
    } catch (error) {
      console.error("[NotificationDropdown] Clear all failed:", error);
    } finally {
      setDismissing(null);
    }
  };

  const handleDeny = async (request: FriendRequest) => {
    const requestId = request.RequestID || request.id || "";
    setActionError(null);
    setProcessing(requestId);
    try {
      const response = await authFetch(`/api/users/follow_requests/${requestId}/deny`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      });

      if (response.ok) {
        await markHubNotificationRead(request.hubNotificationId);
        setFriendRequests((prev) =>
          prev.filter((req) => (req.RequestID || req.id) !== requestId),
        );
        await syncUnreadCount();
      } else {
        const errorData = await response
          .json()
          .catch(() => ({ error: t("notifications.errors.denyFailed") }));
        console.error("[NotificationDropdown] Deny failed:", errorData);
        setActionError(errorData.error || t("notifications.errors.denyFailed"));
      }
    } catch (error) {
      console.error("[NotificationDropdown] Deny failed:", error);
      setActionError(t("notifications.errors.denyError"));
    } finally {
      setProcessing(null);
    }
  };

  const renderDismiss = (notificationId: string, onDismiss: (id: string) => void) => (
    <button
      type="button"
      className="gruv-notification-row__dismiss"
      aria-label={t("notifications.dismiss")}
      disabled={dismissing === notificationId || dismissing === "all"}
      onClick={() => onDismiss(notificationId)}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
        <path d="M6 6l12 12M18 6 6 18" />
      </svg>
    </button>
  );

  const renderActivityRow = (
    notif: HubNotification,
    index: number,
    onDismiss: (id: string) => void,
  ) => {
    const glyph = notificationGlyph(notif.type);
    const showTitle = showsNotificationTitle(notif.title, notif.body);
    const age = formatNotificationAge(notif.created_at, ageLabels, locale);
    return (
      <li
        key={notif.id}
        className={`gruv-notification-row gruv-anim-pop${notif.read ? "" : " gruv-notification-row--unread"}`}
        style={{ "--gruv-anim-delay": `${index * ROW_STAGGER_MS}ms` } as React.CSSProperties}
      >
        <span className={`gruv-notification-row__glyph gruv-notification-row__glyph--${glyph.tone}`}>
          <NotificationGlyphIcon name={glyph.icon} />
        </span>
        <div className="gruv-notification-row__content">
          {showTitle ? <p className="gruv-notification-row__title">{notif.title}</p> : null}
          <p className="gruv-notification-row__body">{notif.body}</p>
          {age ? <span className="gruv-notification-row__age">{age}</span> : null}
        </div>
        {!notif.read ? <span className="gruv-notification-row__dot" aria-hidden="true" /> : null}
        {renderDismiss(notif.id, onDismiss)}
      </li>
    );
  };

  return (
    <div
      className="gruv-notification-dropdown gruv-anim-pop"
      role="dialog"
      aria-label={t("notifications.title")}
    >
      <header className="gruv-notification-dropdown__header">
        <div className="gruv-notification-dropdown__heading">
          <h3>{t("notifications.title")}</h3>
          {totalUnread > 0 && (
            <span className="gruv-notification-dropdown__count">{totalUnread}</span>
          )}
        </div>
        {totalUnread > 0 && (
          <button
            type="button"
            className="gruv-notification-dropdown__clear"
            disabled={dismissing === "all"}
            onClick={() => void handleClearAll()}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M4 7h16M9.5 7V4.5h5V7M6.5 7l.8 12h9.4l.8-12M10 11v5M14 11v5" />
            </svg>
            <span>{t("notifications.clearAll")}</span>
          </button>
        )}
      </header>

      {!loading && totalUnread > 0 ? (
        <p className="gruv-notification-dropdown__hint">
          {t("notifications.unreadHint", { count: totalUnread })}
        </p>
      ) : null}

      {actionError ? (
        <p className="gruv-notification-dropdown__error" role="alert">
          {actionError}
        </p>
      ) : null}

      <div className="gruv-notification-dropdown__body">
        {loading ? (
          <div className="gruv-notification-dropdown__loading">
            <span className="gruv-notification-dropdown__spinner" aria-hidden="true" />
            <p>{t("notifications.loading")}</p>
          </div>
        ) : totalUnread === 0 ? (
          <div className="gruv-notification-dropdown__empty">
            <span className="gruv-notification-dropdown__empty-icon" aria-hidden="true">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 16V11a6 6 0 0 1 9.3-5M18 11v5l1.5 2h-15" />
                <path d="M10 20a2 2 0 0 0 4 0" />
                <path d="M3.5 3.5l17 17" />
              </svg>
            </span>
            <p>{t("notifications.emptyTitle")}</p>
            <span className="gruv-notification-dropdown__empty-subtitle">
              {t("notifications.emptySubtitle")}
            </span>
          </div>
        ) : (
          <>
            {friendRequests.length > 0 && (
              <section className="gruv-notification-dropdown__section">
                <h4 className="gruv-notification-dropdown__section-title">
                  {t("notifications.sections.requests")}
                </h4>
                <ul className="gruv-notification-dropdown__list">
                  {friendRequests.map((request, index) => {
                    const followerAvatarSrc = normalizeAvatarSrc(
                      request.AvatarKey || request.follower_avatar_key,
                    );
                    const requestKey = request.RequestID || request.id || "";
                    const name =
                      request.DisplayName ||
                      request.follower_display_name ||
                      request.Username ||
                      request.follower_username ||
                      "";
                    const busy = processing === requestKey;
                    return (
                      <li
                        key={requestKey}
                        className="gruv-notification-row gruv-notification-row--unread gruv-notification-row--request gruv-anim-pop"
                        style={{ "--gruv-anim-delay": `${index * ROW_STAGGER_MS}ms` } as React.CSSProperties}
                      >
                        <span className="gruv-notification-row__glyph gruv-notification-row__glyph--primary gruv-notification-row__glyph--avatar">
                          {followerAvatarSrc ? (
                            <img src={followerAvatarSrc} alt={name} />
                          ) : (
                            <NotificationGlyphIcon name="person-add" />
                          )}
                        </span>
                        <div className="gruv-notification-row__content">
                          <p className="gruv-notification-row__title">
                            <strong>{name}</strong>{" "}
                            <span className="gruv-notification-row__title-suffix">
                              {t("notifications.requestSuffix")}
                            </span>
                          </p>
                          <div className="gruv-notification-row__actions">
                            <button
                              type="button"
                              className="gruv-notification-row__btn gruv-notification-row__btn--approve"
                              onClick={() => handleApprove(request)}
                              disabled={busy}
                            >
                              {busy ? t("notifications.processing") : t("notifications.approve")}
                            </button>
                            <button
                              type="button"
                              className="gruv-notification-row__btn gruv-notification-row__btn--deny"
                              onClick={() => handleDeny(request)}
                              disabled={busy}
                            >
                              {busy ? t("notifications.processing") : t("notifications.deny")}
                            </button>
                          </div>
                        </div>
                        <span className="gruv-notification-row__dot" aria-hidden="true" />
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}

            {unreadActivity.length > 0 && (
              <section className="gruv-notification-dropdown__section">
                <h4 className="gruv-notification-dropdown__section-title">
                  {t("notifications.sections.activity")}
                </h4>
                <ul className="gruv-notification-dropdown__list">
                  {unreadActivity.map((notif, index) =>
                    renderActivityRow(notif, friendRequests.length + index, handleDismissActivity),
                  )}
                </ul>
              </section>
            )}

            {socialNotifications.length > 0 && (
              <section className="gruv-notification-dropdown__section">
                <h4 className="gruv-notification-dropdown__section-title">
                  {t("notifications.sections.social")}
                </h4>
                <ul className="gruv-notification-dropdown__list">
                  {socialNotifications.map((notif, index) =>
                    renderActivityRow(
                      notif,
                      friendRequests.length + unreadActivity.length + index,
                      handleDismissSocial,
                    ),
                  )}
                </ul>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
